import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { PopoverLayoutComponent } from '../../../map-modules/external-entities/shared/popover-layout/popover-layout.component';
import { XTitleRowComponent } from '../../../shared/x-kit/components/x-title-row/x-title-row.component';
import { XTabPanelComponent } from '../../../shared/x-kit/components/x-tab-panel/x-tab-panel.component';
import { TariffItemComponent } from './tariff-item/tariff-item.component';
import { XDividerComponent } from '../../../shared/x-kit/components/x-divider/x-divider.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Plan, PlansList } from '../../../interfaces';
import { TariffsService } from '../../../shared/services/api/tariffs.service';
import {CurrencyPipe, DatePipe, DecimalPipe, NgIf} from '@angular/common';
import { XOutlinedInputComponent } from '../../../shared/x-kit/components/x-outlined-input/x-outlined-input.component';
import { ComponentsModule } from '../../../shared/ui-kit/components/components.module';
import { XInnerInputComponent } from '../../../shared/x-kit/components/x-inner-input/x-inner-input.component';
import { XButtonComponent } from '../../../shared/x-kit/components/x-button/x-button.component';
import { first, interval, of, Subscription, switchMap, takeWhile } from 'rxjs';
import { XIconButtonComponent } from '../../../shared/x-kit/components/x-icon-button/x-icon-button.component';
import { SelectedWorkspaceStore } from '../../../stores/selected-workspace.store';
import {PaymentOverlayComponent} from "../payment-overlay/payment-overlay.component";
import {TranslatePipe} from "@ngx-translate/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {CustomCurrencyPipe} from "../../../shared/pipes/custom-currency.pipe";
import { MetricService } from '../../../core/services/metric.service';
import { MonetizationAnalyticsService } from '../../../core/services/monetization-analytics.service';
import { SubscribePeriodEnum } from '../../workspace/features/create-workspace/services/workspace-api.service';

export interface DialogData {
  tariffsData: PlansList;
}

@Component({
  selector: 'app-tariff-buy-modal',
  standalone: true,
  imports: [
    PopoverLayoutComponent,
    XTitleRowComponent,
    XTabPanelComponent,
    TariffItemComponent,
    XDividerComponent,
    SvgIconComponent,
    NgIf,
    XOutlinedInputComponent,
    ComponentsModule,
    XInnerInputComponent,
    XButtonComponent,
    CurrencyPipe,
    DatePipe,
    XIconButtonComponent,
    PaymentOverlayComponent,
    TranslatePipe,
    CustomCurrencyPipe,
  ],
  providers: [DecimalPipe, CurrencyPipe],
  templateUrl: './tariff-buy-modal.component.html',
  styleUrl: './tariff-buy-modal.component.less',
  animations: [
    trigger('cardState', [
      state(
        'active',
        style({
          height: '160px',
        }),
      ),
      state(
        'inactive',
        style({
          height: '64px',
        }),
      ),
      transition('inactive <=> active', [animate('0.4s ease-in-out')]),
    ]),
  ],
})
export class TariffBuyModalComponent implements OnInit {
  tariffScreen: 'pricing' | 'order' = 'pricing';

  public discountCode = '';
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef);
  private tariffsService = inject(TariffsService);
  private cdr = inject(ChangeDetectorRef);
  private notify = inject(NzMessageService);
  private selectedWorkspaceStore = inject(SelectedWorkspaceStore);
  private metricService = inject(MetricService);
  private monetizationAnalytics = inject(MonetizationAnalyticsService);

  date30DaysFromToday = new Date().setDate(new Date().getDate() + 30);
  date1YearFromToday = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

  public readonly tariffsData = this.data.tariffsData;

  monthlyYearlyActive: string | number = '1';
  public active = 1;

  public tariffForOrderScreen: Plan | null = null;
  public payLink: string | null = null;

  ngOnInit(): void {
    this.monetizationAnalytics.trackViewPricingFromPlansList(this.tariffsData);
  }

  onContinue() {
    switch (this.monthlyYearlyActive) {
      case '0':
        this.onContinueWithMonthly();
        break;
      case '1':
        this.onContinueWithYearly();
        break;
    }
  }

  onContinueWithMonthly() {
    let tariff;
    switch (this.active) {
      default:
      case 1:
        tariff = this.tariffsData['standart-month'];
        break;
      case 2:
        tariff = this.tariffsData['professional-month'];
    }

    this.tariffForOrderScreen = tariff;
    this.tariffScreen = 'order';
  }

  onContinueWithYearly() {
    let tariff;
    switch (this.active) {
      default:
      case 1:
        tariff = this.tariffsData['standart-year'];
        break;
      case 2:
        tariff = this.tariffsData['professional-year'];
    }

    this.tariffForOrderScreen = tariff;
    this.tariffScreen = 'order';
  }

  buyTariff(plan: Plan | undefined) {
    if (!plan) {
      return;
    }

    const period = plan.isYearly ? SubscribePeriodEnum.YEAR : SubscribePeriodEnum.MONTH;
    const type = plan.type.replace('-year', '').replace('-month', '');

    this.monetizationAnalytics.trackBeginCheckout({
      payLink: this.payLink ?? '',
      subscriptionType: type,
      period,
      seats: 1,
      totalValue: plan.totalPrice,
    });
  }

  public applyDiscount(code: string) {
    if (!code) {
      return;
    }
  }

  public backToTariffScreen() {
    this.tariffScreen = 'pricing';
    this.tariffForOrderScreen = null;
    this.discountCode = '';
  }

  private paymentCheckSubscription: Subscription | null = null;

  startCheckProcess() {
    if (!this.payLink) {
      return;
    }

    // Начинаем проверку статуса оплаты каждые 5 секунд
    this.paymentCheckSubscription = interval(5000) // каждые 5000 мс
      .pipe(
        switchMap(() => {
          if (!this.payLink) {
            return of(null);
          }
          return this.tariffsService.checkPayStatus(this.payLink);
        }), // делаем запрос
        takeWhile((status) => !status, true), // продолжаем, пока статус не станет success
      )
      .subscribe({
        next: (status) => console.log(`Pay status: ${status}`),
        complete: () => {
          const payLink = this.payLink ?? '';
          if (payLink) {
            this.monetizationAnalytics.trackPurchaseComplete({ payLink });
          }

          this.payLink = '';
          this.selectedWorkspaceStore.refreshUser();
          this.dialogRef.close({ success: true });
        },
        error: (error) => {
          this.monetizationAnalytics.trackPaymentFailed({
            error: error.message || 'Payment check failed',
          });
        },
      });
  }

  ngOnDestroy() {
    this.paymentCheckSubscription?.unsubscribe();
  }

  public changeMontlyYearly(event: string | number) {
    this.monthlyYearlyActive = event;
    this.cdr.detectChanges();
  }

  public openLink() {
    if (!this.payLink) {
      return;
    }

    this.metricService.track({ name: 'payment_tariff-dialog_manual-pay-click' });

    window.open(this.payLink, '_blank');
  }
}
